import React from 'react';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  Grid,
  Heading,
  HStack,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack
} from '@chakra-ui/react';
import { SETTLEMENT_TIMELINE_MANAGER_STRINGS, TAB_STATES } from '@views/SettlementTimelineManager/constant';
import { ROLES } from '@constants';
import { MdEdit, MdOutlineCancel } from 'react-icons/md';
import { IoMdCheckmark } from 'react-icons/io';
import { AmountRangeTable } from './components';
import DynamicFormField from '@components/DynamicFormField/DynamicFormField';
import { INPUT_FIELD_TYPE } from '@components/DynamicFormField/constant';

// Header Section
export const HeaderSection = ({
  hasUnsavedChanges,
  hasAnyAuthority,
  isEditMode,
  handleEditMode,
  handleCancelEdit,
  handleSave,
  timeUnit,
  handleTimeUnitChange
}) => (
  <VStack alignItems="stretch" gap={4}>
    <HStack justify="space-between" align="center">
      <Box>
        <Heading size="lg" color="colorPrimary">
          {SETTLEMENT_TIMELINE_MANAGER_STRINGS.STRING_CONSTANTS[TAB_STATES.NEW_FULFILLMENT_TIMELINES_V2].HEADING}
        </Heading>
        <Text color="gray.400" fontSize="sm" mt={1}>
          Configure fulfillment timeline settings across countries and beneficiary types
        </Text>
      </Box>
      <HStack>
        {hasUnsavedChanges && (
          <Badge colorScheme="yellow" fontSize="sm" px={3} py={1}>
            Unsaved Changes
          </Badge>
        )}
        {hasAnyAuthority(ROLES.CONFIG_MANAGER_ROLE) && !isEditMode && (
          <Button
            colorScheme="brand"
            size="md"
            bg="brand.600"
            _hover={{ bg: 'brand.700' }}
            onClick={handleEditMode}
            leftIcon={<MdEdit size="1.2rem" />}
          >
            Edit
          </Button>
        )}
        {isEditMode && (
          <HStack spacing={2}>
            <Button
              colorScheme="red"
              variant="outline"
              size="md"
              bg="red.900"
              borderColor="red.500"
              _hover={{ bg: 'red.800' }}
              onClick={handleCancelEdit}
              leftIcon={<MdOutlineCancel size="1.2rem" />}
            >
              Cancel
            </Button>
            <Button
              colorScheme="brand"
              size="md"
              bg="brand.600"
              _hover={{ bg: 'brand.700' }}
              onClick={handleSave}
              leftIcon={<IoMdCheckmark size="1.2rem" />}
            >
              Save
            </Button>
          </HStack>
        )}
      </HStack>
    </HStack>

    {/* Time Unit Toggle */}
    <HStack justify="flex-start">
      <Text fontSize="sm" fontWeight="semibold" color="gray.300">
        View in:
      </Text>
      <RadioGroup
        value={timeUnit}
        gap={4}
        py={2}
        px={4}
        bg="dark.400"
        borderRadius="xl"
        onChange={handleTimeUnitChange}
      >
        <Stack direction="row" gap={2}>
          <Radio value="minutes" colorScheme="brand">Minutes</Radio>
          <Radio value="hours" colorScheme="brand">Hours</Radio>
        </Stack>
      </RadioGroup>
    </HStack>
  </VStack>
);

// Version Selector Section
export const VersionSelectorSection = ({
  selectedVersion,
  setSelectedVersion,
  isEditMode,
  handleLoadVersion
}) => (
  <Box p={4} borderRadius="xl" bg="backGround" borderWidth="1px" borderColor="whiteAlpha.300">
    <FormControl maxW="250px">
      <FormLabel fontSize="sm" fontWeight="semibold" color="gray.300">
        Configuration Version
      </FormLabel>
      <Select
        value={selectedVersion}
        onChange={(e) => {
          const newVersion = e.target.value;
          setSelectedVersion(newVersion);
          handleLoadVersion(newVersion);
        }}
        bg="dark.400"
        borderColor="whiteAlpha.500"
        color='white'
        isDisabled={isEditMode}
        _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
      >
        <option value="1">Version 1</option>
      </Select>
    </FormControl>
  </Box>
);

// Global Configuration Section
export const GlobalConfigurationSection = ({
  formData,
  isEditMode,
  handleTopLevelChange,
  timeUnit,
  getDisplayValue,
  convertInputToMinutes
}) => (
  <Box position={{ xl: 'sticky' }} top="20px" h="fit-content">
    <Card bg="backGround" borderColor="brand.500" borderWidth="2px" shadow="xl">
      <CardBody>
        <VStack alignItems="stretch" gap={4}>
          <Heading size="md" color="colorPrimary">
            Global Configuration
          </Heading>
          <Text fontSize="xs" color="gray.400">
            Applies to all countries and beneficiary types
          </Text>

          <DynamicFormField
            fieldConfig={{
              name: 'version',
              label: 'Version',
              fieldType: INPUT_FIELD_TYPE.INPUT,
              inputType: 'text',
              isRequired: false,
              isEditable: false,
              size: 'sm',
              width: '100%',
              labelColor: 'gray.300'
            }}
            data={{ version: formData.version || '' }}
            handleChange={() => {}}
          />

          <DynamicFormField
            fieldConfig={{
              name: 'isPollingEnabled',
              label: 'Polling Enabled',
              fieldType: INPUT_FIELD_TYPE.SWITCH,
              isRequired: false,
              isEditable: isEditMode,
              size: 'md',
              labelColor: 'gray.300'
            }}
            data={{ isPollingEnabled: formData.isPollingEnabled || false }}
            handleChange={(e) =>
              handleTopLevelChange('isPollingEnabled', e.target.checked)
            }
          />

        <DynamicFormField
          fieldConfig={{
            name: 'isGlobalEtaEnabled',
            label: 'Global ETA Enabled',
            fieldType: INPUT_FIELD_TYPE.SWITCH,
            isRequired: false,
            isEditable: isEditMode,
            size: 'md',
            labelColor: 'gray.300'
          }}
          data={{ isGlobalEtaEnabled: formData.isGlobalEtaEnabled || false }}
          handleChange={(e) => handleTopLevelChange('isGlobalEtaEnabled', e.target.checked)}
        />

          <DynamicFormField
            fieldConfig={{
              name: 'globalEtaInMinutes',
              label: `Global ETA (${timeUnit === 'hours' ? 'hours' : 'minutes'})`,
              fieldType: INPUT_FIELD_TYPE.INPUT,
              inputType: 'number',
              placeholder: 'Enter ETA',
              isRequired: false,
              isEditable: isEditMode,
              size: 'sm',
              width: '100%',
              labelColor: 'gray.300'
            }}
            data={{ globalEtaInMinutes: getDisplayValue(formData.globalEtaInMinutes) }}
            handleChange={(e) => handleTopLevelChange('globalEtaInMinutes', convertInputToMinutes(e.target.value))}
          />
        </VStack>
      </CardBody>
    </Card>
  </Box>
);

// Country Configuration Section
export const CountryConfigurationSection = ({
  countries,
  selectedCountry,
  setSelectedCountry,
  formData,
  isEditMode,
  handleCountryLevelChange,
  handleBeneficiaryLevelChange,
  handleRangeChange,
  handleAddRange,
  timeUnit,
  getDisplayValue,
  convertInputToMinutes
}) => (
  <Box>
    {countries.length > 0 && (
      <Box>
        <Heading size="md" color="colorPrimary" mb={4}>
          Country-Specific Configuration
        </Heading>
        <Box borderRadius="xl" bg="backGround" borderWidth="1px" borderColor="whiteAlpha.300">
          <Tabs
            variant="enclosed"
            colorScheme="brand"
            index={countries.indexOf(selectedCountry)}
            onChange={(index) => setSelectedCountry(countries[index])}
          >
            <TabList borderColor="whiteAlpha.500" overflowX="auto" flexWrap="wrap" gap={2}>
              {countries.map((country) => (
                <Tab
                  key={country}
                  fontWeight="semibold"
                  fontSize="sm"
                  color="gray.100"
                  _selected={{
                    color: 'white',
                    bg: 'brand.600',
                    borderColor: 'brand.500',
                  }}
                  _hover={{ bg: 'dark.500', color: 'white' }}
                >
                  {country}
                </Tab>
              ))}
            </TabList>

              <TabPanels>
                {countries.map((country) => (
                  <TabPanel key={country} p={6}>
                    <VStack alignItems="stretch" gap={6}>
                      {/* Country Level Settings */}
                      <Card bg="dark.400" borderColor="whiteAlpha.400" borderWidth="1px">
                        <CardBody>
                          <Heading size="sm" color="colorPrimary" mb={4}>
                            Country Settings
                          </Heading>
                          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4} alignItems="end">
                            <DynamicFormField
                              fieldConfig={{
                                name: 'globalEtaInMinutes',
                                label: `Country Global ETA (${timeUnit === 'hours' ? 'hours' : 'minutes'})`,
                                fieldType: INPUT_FIELD_TYPE.INPUT,
                                inputType: 'number',
                                placeholder: 'Enter ETA',
                                isRequired: false,
                                isEditable: isEditMode,
                                width: '50%',
                                labelColor: 'gray.300'
                              }}
                              data={{ globalEtaInMinutes: getDisplayValue(formData.config.countries[country].globalEtaInMinutes) }}
                              handleChange={(e) =>
                                handleCountryLevelChange(
                                  country,
                                  'globalEtaInMinutes',
                                  convertInputToMinutes(e.target.value)
                                )
                              }
                            />
                            <DynamicFormField
                              fieldConfig={{
                                name: 'isGlobalEtaEnabled',
                                label: 'Country Global ETA Enabled',
                                fieldType: INPUT_FIELD_TYPE.SWITCH,
                                isRequired: false,
                                isEditable: isEditMode,
                                size: 'lg',
                                labelColor: 'gray.300'
                              }}
                              data={{ isGlobalEtaEnabled: formData.config.countries[country].isGlobalEtaEnabled || false }}
                              handleChange={(e) =>
                                handleCountryLevelChange(country, 'isGlobalEtaEnabled', e.target.checked)
                              }
                            />
                          </Grid>
                        </CardBody>
                      </Card>

                      {/* Beneficiary Types */}
                      <Box>
                        <Heading size="sm" color="colorPrimary" mb={4}>
                          Beneficiary Types
                        </Heading>

                        <Accordion
                          allowMultiple
                          defaultIndex={Object.keys(formData.config.countries[country].beneficiaryTypes || {}).map((_, index) => index)}
                        >
                          {Object.keys(formData.config.countries[country].beneficiaryTypes || {}).map(
                            (beneficiaryType) => (
                              <AccordionItem
                                key={beneficiaryType}
                                border="1px solid"
                                borderColor="whiteAlpha.400"
                                borderRadius="md"
                                mb={3}
                                bg="dark.400"
                              >
                                <AccordionButton 
                                  _hover={{ bg: 'dark.500' }}
                                  py={4}
                                >
                                  <Box flex="1" textAlign="left">
                                    <Text fontWeight="bold" fontSize="md" color="white">
                                      {beneficiaryType}
                                    </Text>
                                  </Box>
                                  <AccordionIcon color="brand.400" />
                                </AccordionButton>
                                <AccordionPanel bg="dark.300" p={6}>
                                  <VStack alignItems="stretch" gap={6}>
                                    {/* Beneficiary Level Settings */}
                                    <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4} alignItems="end">
                                      <DynamicFormField
                                        fieldConfig={{
                                          name: 'globalEtaInMinutes',
                                          label: `Beneficiary Global ETA (${timeUnit === 'hours' ? 'hours' : 'minutes'})`,
                                          fieldType: INPUT_FIELD_TYPE.INPUT,
                                          inputType: 'number',
                                          placeholder: 'Enter ETA',
                                          isRequired: false,
                                          isEditable: isEditMode,
                                          width: '100%',
                                          labelColor: 'gray.300'
                                        }}
                                        data={{
                                          globalEtaInMinutes: getDisplayValue(formData.config.countries[country].beneficiaryTypes[beneficiaryType]
                                            .globalEtaInMinutes)
                                        }}
                                        handleChange={(e) =>
                                          handleBeneficiaryLevelChange(
                                            country,
                                            beneficiaryType,
                                            'globalEtaInMinutes',
                                            convertInputToMinutes(e.target.value)
                                          )
                                        }
                                      />
                                      <DynamicFormField
                                        fieldConfig={{
                                          name: 'isGlobalEtaEnabled',
                                          label: 'Beneficiary Global ETA Enabled',
                                          fieldType: INPUT_FIELD_TYPE.SWITCH,
                                          isRequired: false,
                                          isEditable: isEditMode,
                                          size: 'lg',
                                          labelColor: 'gray.300'
                                        }}
                                        data={{
                                          isGlobalEtaEnabled: formData.config.countries[country].beneficiaryTypes[beneficiaryType]
                                            .isGlobalEtaEnabled || false
                                        }}
                                        handleChange={(e) =>
                                          handleBeneficiaryLevelChange(
                                            country,
                                            beneficiaryType,
                                            'isGlobalEtaEnabled',
                                            e.target.checked
                                          )
                                        }
                                      />
                                    </Grid>

                                    {/* Amount Ranges Table */}
                                    <AmountRangeTable
                                      ranges={
                                        formData.config.countries[country].beneficiaryTypes[
                                          beneficiaryType
                                        ].receiveAmountRange
                                      }
                                      country={country}
                                      beneficiaryType={beneficiaryType}
                                      isEditMode={isEditMode}
                                      onRangeChange={handleRangeChange}
                                      onAddRange={handleAddRange}
                                      timeUnit={timeUnit}
                                      getDisplayValue={getDisplayValue}
                                      convertInputToMinutes={convertInputToMinutes}
                                    />
                                  </VStack>
                                </AccordionPanel>
                              </AccordionItem>
                            )
                          )}
                        </Accordion>
                      </Box>
                    </VStack>
                  </TabPanel>
                ))}
              </TabPanels>
            </Tabs>
        </Box>
      </Box>
    )}
  </Box>
);
